// Owner: shell-nav (wave 2); registry conversion by ui-layers. Screen `command-palette`: ⌘K / Ctrl+K
// anywhere (the `app.palette` command), the rail's "Search or ask…" button and the mobile Search tab
// open it through `openLayer('palette', { query? })`. The command bar is a lazy chunk: the shell
// preloads it when the browser is idle. On a cold open the bar's frame with a focused input shows at
// once, and what the member types there becomes the query of the full bar (no lost keystrokes).
import { Suspense, lazy, useRef, useState } from 'react';
import { Dialog, Icon } from '@dataroom/ui';
import { t } from '../lib/i18n';
import { type GlobalLayerProps, defineGlobalLayer } from '../shell/overlays';
import { paletteOpener } from './palette-typeahead';

const load = () => import('./command-palette.layer');
const CommandPaletteLayer = lazy(load);

let preloaded = false;
/** Fetch the command bar's chunk once the browser is idle (the app shell calls it on mount). */
export function preloadCommandPalette(): void {
  if (preloaded || typeof window === 'undefined') return;
  preloaded = true;
  const run = () => void load().catch(() => (preloaded = false));
  if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 4_000 });
  else setTimeout(run, 1_500);
}

/** The query typed into the frame before the full bar existed (`null`: nothing typed). */
export type Typeahead = { take: () => string | null };

/** The element focused before the bar opened: its commands are the ones that work there. */
function originElement(): Element | null {
  if (typeof document === 'undefined') return null;
  const el = paletteOpener() ?? document.activeElement;
  return el && el !== document.body && !el.closest('[data-testid="command-palette"]') ? el : null;
}

/** The bar's body while its chunk loads: same look, a real focused input that keeps what is typed. */
function PaletteFrame({ initial, onText }: { initial: string; onText: (v: string) => void }) {
  return (
    <div className="pal-body" data-testid="command-palette-loading">
      <div className="cmdl">
        <div className="cmdl-head">
          <span className="cmdl-input">
            <Icon name="search" size={16} />
            <input
              aria-label={t('shell.cmdk.label')}
              placeholder={t('shell.cmdk.placeholder')}
              defaultValue={initial}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              data-dirty-ignore=""
              onChange={(e) => onText(e.target.value)}
            />
            <span className="spinner" role="status" aria-label={t('common.loading')} />
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * One dialog for the whole life of the bar: the frame and the full bar are its content in turn, so a
 * cold open neither flashes a second scrim nor plays the open animation twice, and focus moves from
 * the frame's input straight to the bar's.
 */
function CommandPaletteHost(layer: GlobalLayerProps<'palette'>) {
  const initial = layer.props.query ?? '';
  // Read while the page still has focus (the dialog takes it once it opens).
  const [origin] = useState(originElement);
  const typed = useRef<string | null>(null);
  // Read, not emptied: a development render may call the initializer twice and the second call must see the
  // text as well (the host lives only as long as the bar is open, so nothing stale is left behind).
  const typeahead: Typeahead = { take: () => typed.current };
  return (
    <Dialog
      open
      bare
      kind="palette"
      onClose={layer.close}
      title={t('shell.cmdk.label')}
      label={t('shell.cmdk.label')}
      className="pal"
      viewportClassName="pal-scrim"
      data-testid="command-palette"
    >
      <Suspense fallback={<PaletteFrame initial={initial} onText={(v) => (typed.current = v)} />}>
        <CommandPaletteLayer {...layer} typeahead={typeahead} origin={origin} />
      </Suspense>
    </Dialog>
  );
}

export default defineGlobalLayer({
  id: 'palette',
  component: CommandPaletteHost,
});
